/**
 * useCompleteAuditDialog Hook
 *
 * Custom hook to manage complete audit dialog state and handlers.
 */

import { useCompleteAuditSession } from '@internal-audit/hooks/useCompleteAuditSession';
import { useCallback, useState } from 'react';

interface UseCompleteAuditDialogProps {
  /** The audit session to complete */
  session: { id: string } | null;
}

export function useCompleteAuditDialog({ session }: UseCompleteAuditDialogProps) {
  const { completeAuditSession } = useCompleteAuditSession();
  const [isCompleteDialogOpen, setIsCompleteDialogOpen] = useState(false);

  // Handle complete audit action - opens dialog first
  const handleCompleteAudit = useCallback(() => {
    setIsCompleteDialogOpen(true);
  }, []);

  // Handle complete audit confirmation - actual mutation
  const handleCompleteAuditConfirm = useCallback(async () => {
    if (!session?.id) return;

    try {
      const result = await completeAuditSession(session.id);

      if (result.success) {
        setIsCompleteDialogOpen(false);
        // Refresh the page to show updated session status
        window.location.reload();
      } else {
        // Error handling is done within the hook
      }
    } catch {
      // Error handling is done within the hook
    }
  }, [session?.id, completeAuditSession]);

  // Handle complete audit dialog close
  const handleCompleteDialogClose = useCallback(() => {
    setIsCompleteDialogOpen(false);
  }, []);

  return {
    isCompleteDialogOpen,
    handleCompleteAudit,
    handleCompleteAuditConfirm,
    handleCompleteDialogClose,
  };
}
